import { useEffect, useRef } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { formatMoney, type Centavos } from '@abastece/shared';
import type { Producto, UnidadVenta } from '@abastece/core';
import { db } from '../db/dexie.js';
import { listaPredeterminada, precioDe, unidadesDe } from '../lib/catalogo.js';
import { ENTRE_TECLAS_MS } from '../lib/lector-usb.js';
import { useT } from '../i18n/index.js';
import { etiquetaUnidad } from '../i18n/dominio.js';
import { useNombreProducto } from '../i18n/productos.js';
import { Chip, Ico } from './Iconos.js';
import { Miniatura } from './Miniatura.js';

/** A product a code answers to, and the presentation when the code is that one's own. */
export interface Hallado {
  producto: Producto;
  unidad?: UnidadVenta;
}

/**
 * ¿Cuál es? A code more than one product answers to, asked about in a window that has to be answered.
 *
 * Two ways it happens: the same goods registered twice, or a factory that prints one code on two
 * different products (the owner, 2026-10-05, about two pinolillos). Either way nothing is guessed: a
 * guess is a wrong price on the receipt and two stock counts off by one. So each product is a big
 * button with what tells them apart at a glance, the photo, the name, the size the code rings up and
 * what it sells for, and the one in the hand is tapped.
 *
 * With a keyboard, its number picks it too. A USB scanner types digits as well, a few milliseconds
 * apart, so a number counts only when no key came just before it and none follows it at a scanner's
 * pace (`ENTRE_TECLAS_MS`): the next can scanned while this is up picks nothing, and goes nowhere else
 * either (`lib/lector-usb.ts`). The cursor comes into the window, out of the box behind it, so that a
 * scanner's Enter presses none of these buttons.
 */
export function CualEs({
  codigo,
  hallados,
  listaId,
  titulo = false,
  onElegido,
  onSeguir,
}: {
  codigo: string;
  hallados: readonly Hallado[];
  /** Whose prices to show: the sale's list, or the order's. The shop's own when not given. */
  listaId?: string;
  /** Its own heading, where nothing above it says what is asked: over the camera's picture. */
  titulo?: boolean;
  onElegido: (hallado: Hallado) => void;
  /** Answered without choosing: back to scanning. */
  onSeguir: () => void;
}) {
  const t = useT();
  const nombre = useNombreProducto();
  const unidades = useLiveQuery(() => db.unidades.toArray(), [], []);
  // Undefined until read: a price said before then would be «Sin precio» for an instant, which a
  // cashier glancing at it takes for the truth.
  const precios = useLiveQuery(() => db.precios.toArray(), []);
  const listas = useLiveQuery(() => db.listas.toArray(), []);
  const lista = listaId || (listas && listaPredeterminada(listas)?.id);
  const raiz = useRef<HTMLDivElement>(null);
  // Not scrolled to: with three products the window is taller than a phone, and starts at its top.
  useEffect(() => raiz.current?.focus({ preventScroll: true }), []);
  const conNumero = hallados.length <= 9;
  useTeclaDeOpcion(conNumero ? hallados.length : 0, (i) => {
    const elegido = hallados[i];
    if (elegido) onElegido(elegido);
  });

  return (
    <div ref={raiz} tabIndex={-1} className="flex flex-col gap-4 outline-none" data-cual-es>
      {titulo && <h3 className="text-lg font-semibold">{t('cualEs.titulo')}</h3>}
      <div className="flex items-start gap-3">
        <Chip icono="escanear" tinte="marca" />
        <div className="min-w-0 self-center">
          <p className="font-medium">{t('escaner.varios', { n: hallados.length })}</p>
          <p className="tabular mt-0.5 text-sm break-all text-slate-400">{codigo}</p>
        </div>
      </div>
      <ul className="flex flex-col gap-2">
        {hallados.map((hallado, i) => {
          const { producto: p, unidad } = hallado;
          // What choosing it rings up: the presentation the code is printed on, or the product's first.
          const u = unidad ?? unidadesDe(unidades, p.id)[0];
          const precio = precios && listas ? (u && lista ? precioDe(precios, u.id, lista) : null) : undefined;
          const tamano = u ? etiquetaUnidad(u.nombre, u.nombre) : etiquetaUnidad(p.unidadBase, p.unidadBase);
          return (
            <li key={`${p.id}:${unidad?.id ?? ''}`}>
              <button
                type="button"
                className="tarjeta-accion flex w-full items-center gap-3 p-3 text-left"
                onClick={() => onElegido(hallado)}
                data-sin-icono="fila"
                data-opcion={i + 1}
              >
                {/* Its key, where there is a keyboard to press it on. */}
                {conNumero && (
                  <kbd className="tabular hidden size-7 shrink-0 items-center justify-center rounded-lg bg-slate-800 text-sm font-semibold text-slate-300 ring-1 ring-white/10 ring-inset sm:flex">
                    {i + 1}
                  </kbd>
                )}
                <Miniatura producto={p} tamano="md" />
                <span className="min-w-0 flex-1">
                  <span className="block font-medium break-words">{nombre(p)}</span>
                  <span className="tabular block text-sm text-slate-400">
                    {tamano}
                    {precio !== undefined && ` · ${precio !== null ? formatMoney(precio as Centavos) : t('pos.sinPrecio')}`}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <p className="text-xs text-slate-500">{t('escaner.variosAyuda')}</p>
      <button type="button" className="btn-secundario" onClick={onSeguir}>
        <Ico icono="escanear" />
        {t('escaner.seguir')}
      </button>
    </div>
  );
}

/**
 * The option on a number key, pressed by somebody: never one of a USB scanner's digits. A key is
 * a scanner's when another came within `ENTRE_TECLAS_MS` before it, or comes within it after, so
 * the choice is made once that much has passed with nothing else typed.
 */
function useTeclaDeOpcion(cuantas: number, elegir: (indice: number) => void) {
  const alElegir = useRef(elegir);
  alElegir.current = elegir;
  useEffect(() => {
    if (cuantas === 0) return;
    let ultima = Number.NEGATIVE_INFINITY;
    let pendiente: number | undefined;
    function alTeclear(e: KeyboardEvent) {
      const seguida = e.timeStamp - ultima <= ENTRE_TECLAS_MS;
      ultima = e.timeStamp;
      window.clearTimeout(pendiente);
      pendiente = undefined;
      if (seguida || e.repeat || e.ctrlKey || e.metaKey || e.altKey || e.isComposing) return;
      if (!/^[1-9]$/.test(e.key) || Number(e.key) > cuantas) return;
      // A number typed into a field is that field's.
      const en = e.target;
      if (en instanceof HTMLInputElement || en instanceof HTMLTextAreaElement || en instanceof HTMLSelectElement) return;
      const indice = Number(e.key) - 1;
      pendiente = window.setTimeout(() => alElegir.current(indice), ENTRE_TECLAS_MS + 30);
    }
    // Capturing, so a key a screen swallows on its way down is still seen to have been pressed.
    window.addEventListener('keydown', alTeclear, true);
    return () => {
      window.removeEventListener('keydown', alTeclear, true);
      window.clearTimeout(pendiente);
    };
  }, [cuantas]);
}
